import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LayoutGrid, List, Plus, Layers } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { PageToolbar } from '@/components/layout/PageToolbar'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PageError } from '@/components/ui/PageAsyncState'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import {
  computeBomListKpis,
  groupBomsByProductAndMode,
} from '@/features/boms/bomListMetrics'
import {
  BOM_STATUS_LABEL,
  bomService,
  type Bom,
  type BomStatus,
} from '@/services/boms'

const fieldClass =
  'h-8 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 text-sm outline-none focus:border-[var(--color-accent)]'

export function BomListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Bom[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<'' | BomStatus>('')
  const [viewMode, setViewMode] = useState<'grouped' | 'table'>('grouped')

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      setRows(await bomService.list())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được danh sách BOM')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const kpis = useMemo(() => computeBomListKpis(rows), [rows])

  const filtered = useMemo(() => {
    if (!statusFilter) return rows
    return rows.filter((r) => r.status === statusFilter)
  }, [rows, statusFilter])

  const grouped = useMemo(
    () => groupBomsByProductAndMode(filtered),
    [filtered],
  )

  const columns: ProtoColumn<Bom>[] = useMemo(
    () => [
      {
        id: 'code_hint',
        header: 'Mã gợi ý',
        sortable: true,
        cell: (r) => (
          <span className="font-mono text-xs font-semibold text-slate-800">
            {r.code_hint || '—'}
          </span>
        ),
        sortValue: (r) => r.code_hint ?? '',
      },
      {
        id: 'assembly_type',
        header: 'Chế độ',
        sortable: true,
        cell: (r) => (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              r.assembly_type === 'assembled'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : r.assembly_type === 'kd'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'bg-slate-100 text-slate-600'
            }`}
          >
            {r.assembly_type === 'assembled'
              ? 'RÁP CHẾT (FA)'
              : r.assembly_type === 'kd'
                ? 'Tháo rời (KD)'
                : '—'}
          </span>
        ),
        sortValue: (r) => r.assembly_type ?? '',
      },
      {
        id: 'name',
        header: 'Tên',
        sortable: true,
        hideable: false,
        width: 220,
        cell: (r) => <span className="font-medium">{r.name}</span>,
        sortValue: (r) => r.name,
      },
      {
        id: 'version_label',
        header: 'Version',
        sortable: true,
        cell: (r) => (
          <span>
            {r.version_label}{' '}
            <span className="text-slate-400">
              (v{r.version_seq}
              {r.edit_count ? ` · sửa ${r.edit_count}` : ''})
            </span>
          </span>
        ),
        sortValue: (r) => r.version_seq,
      },
      {
        id: 'status',
        header: 'Trạng thái',
        sortable: true,
        cell: (r) => (
          <StatusBadge
            tone={r.status === 'active' ? 'success' : 'neutral'}
            label={BOM_STATUS_LABEL[r.status]}
            showDot={false}
          />
        ),
        sortValue: (r) => r.status,
      },
      {
        id: 'product_id',
        header: 'Product',
        cell: (r) =>
          r.product_id ? (
            <button
              type="button"
              className="font-mono text-xs text-[var(--color-link)] hover:underline"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/products/${r.product_id}`)
              }}
            >
              {r.product_id}
            </button>
          ) : (
            '—'
          ),
        sortValue: (r) => r.product_id ?? '',
      },
    ],
    [navigate],
  )

  return (
    <div className="space-y-4">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Danh mục', to: '/catalog' },
          { label: 'BOM / Định mức' },
        ]}
      />
      <PageHeader
        title="BOM / Định mức"
        subtitle="Quản lý BOM sản phẩm theo Mã hàng và Chế độ lắp ráp (Ráp chết / Tháo rời)"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate('/boms/review-names')}
            >
              Rà soát tên BOM
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/purchasing/standard-names')}
            >
              Từ điển tên chuẩn
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/purchasing/boms/intake')}
            >
              Import từ Excel
            </Button>
            <Button onClick={() => navigate('/purchasing/boms/new')}>
              <Plus className="h-4 w-4" />
              Tạo BOM mới
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Tổng BOM" value={kpis.total} />
        <KpiCard label="Active" value={kpis.active} tone="success" />
        <KpiCard
          label="Inactive"
          value={kpis.inactive}
          tone={kpis.inactive ? 'warning' : 'default'}
        />
        <KpiCard
          label="Mã hàng có BOM"
          value={grouped.length}
          tone="default"
        />
      </div>

      {error ? (
        <PageError message={error} onRetry={() => void reload()} />
      ) : null}

      <PageToolbar
        left={
          <div className="flex items-center gap-2">
            <select
              className={fieldClass}
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as '' | BomStatus)
              }
              aria-label="Lọc trạng thái"
            >
              <option value="">Tất cả trạng thái</option>
              <option value="active">ACTIVE</option>
              <option value="inactive">INACTIVE</option>
              <option value="archived">ARCHIVED</option>
            </select>
          </div>
        }
        right={
          <div className="flex items-center rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white p-0.5 text-xs">
            <button
              type="button"
              className={`flex items-center gap-1 px-2.5 py-1 rounded transition-colors ${
                viewMode === 'grouped'
                  ? 'bg-slate-100 font-semibold text-slate-800'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setViewMode('grouped')}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              <span>Gom theo Mã hàng</span>
            </button>
            <button
              type="button"
              className={`flex items-center gap-1 px-2.5 py-1 rounded transition-colors ${
                viewMode === 'table'
                  ? 'bg-slate-100 font-semibold text-slate-800'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setViewMode('table')}
            >
              <List className="h-3.5 w-3.5" />
              <span>Bảng danh sách</span>
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="p-8 text-center text-xs text-slate-400">
          Đang tải dữ liệu…
        </div>
      ) : viewMode === 'grouped' ? (
        <div className="space-y-4">
          {grouped.length === 0 ? (
            <div className="rounded-[var(--radius-md)] border border-dashed border-slate-200 p-8 text-center text-xs text-slate-500 bg-slate-50">
              Không tìm thấy BOM nào phù hợp bộ lọc.
            </div>
          ) : (
            grouped.map((group) => (
              <section
                key={group.groupKey}
                className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700 font-bold text-xs font-mono">
                      {group.codeHint || 'SP'}
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                        <span>{group.codeHint || group.productId || 'Chưa gán mã'}</span>
                        <span className="text-xs font-normal text-slate-500 font-mono">
                          ({group.totalBoms} phiên bản BOM)
                        </span>
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {group.productId ? (
                      <>
                        <Link
                          to={`/products/${group.productId}/matrix`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-indigo-200 bg-indigo-50 text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
                        >
                          <Layers className="h-3.5 w-3.5" />
                          <span>Ma trận điều kiện</span>
                        </Link>
                        <Link
                          to={`/products/${group.productId}`}
                          className="inline-flex items-center px-2 py-1 text-xs text-slate-600 hover:text-slate-900 hover:underline"
                        >
                          Chi tiết sản phẩm →
                        </Link>
                      </>
                    ) : null}
                  </div>
                </div>

                {/* Two columns for Mode: Assembled and KD */}
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* ASSEMBLED BOMs */}
                  <div className="rounded-lg border border-blue-100 bg-blue-50/30 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-blue-600" />
                        Ráp chết (assembled)
                      </span>
                      <span className="text-xs font-mono text-blue-700">
                        {group.assembledBoms.length} bản ghi
                      </span>
                    </div>

                    {group.assembledBoms.length === 0 ? (
                      <p className="text-xs text-slate-400 italic py-2">
                        Chưa có BOM ráp chết cho sản phẩm này.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {group.assembledBoms.map((b) => (
                          <div
                            key={b.id}
                            onClick={() => navigate(`/purchasing/boms/${b.id}`)}
                            className="flex items-center justify-between rounded border border-slate-200 bg-white p-2.5 text-xs hover:border-indigo-400 hover:shadow-xs transition-all cursor-pointer"
                          >
                            <div>
                              <p className="font-semibold text-slate-800">
                                {b.name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {b.version_label} (v{b.version_seq})
                                {b.edit_count ? ` · Đã sửa lần ${b.edit_count}` : ''}
                              </p>
                            </div>
                            <StatusBadge
                              tone={b.status === 'active' ? 'success' : 'neutral'}
                              label={BOM_STATUS_LABEL[b.status]}
                              showDot={false}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* KD BOMs */}
                  <div className="rounded-lg border border-purple-100 bg-purple-50/30 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-purple-900 uppercase tracking-wide flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-purple-600" />
                        Tháo rời (kd)
                      </span>
                      <span className="text-xs font-mono text-purple-700">
                        {group.kdBoms.length} bản ghi
                      </span>
                    </div>

                    {group.kdBoms.length === 0 ? (
                      <p className="text-xs text-slate-400 italic py-2">
                        Chưa có BOM tháo rời cho sản phẩm này.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {group.kdBoms.map((b) => (
                          <div
                            key={b.id}
                            onClick={() => navigate(`/purchasing/boms/${b.id}`)}
                            className="flex items-center justify-between rounded border border-slate-200 bg-white p-2.5 text-xs hover:border-indigo-400 hover:shadow-xs transition-all cursor-pointer"
                          >
                            <div>
                              <p className="font-semibold text-slate-800">
                                {b.name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {b.version_label} (v{b.version_seq})
                                {b.edit_count ? ` · Đã sửa lần ${b.edit_count}` : ''}
                              </p>
                            </div>
                            <StatusBadge
                              tone={b.status === 'active' ? 'success' : 'neutral'}
                              label={BOM_STATUS_LABEL[b.status]}
                              showDot={false}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            ))
          )}
        </div>
      ) : (
        <ProtoExcelTable
          columns={columns}
          rows={filtered}
          rowKey={(r) => r.id}
          searchPlaceholder="Tìm tên, mã gợi ý…"
          exportName="dinh-muc-bom"
          pageSize={50}
          loading={loading}
          emptyMessage="Chưa có BOM nào."
          onRowClick={(r) => navigate(`/purchasing/boms/${r.id}`)}
          stickyLeftColumnIds={['name']}
        />
      )}
    </div>
  )
}
